/**
 * Add from the field on the tech's job page (approved mockup steps 1, D, V):
 *
 *   FieldAddButton        the green + left of the blue Message bubble, same
 *                         size, stays put while you scroll
 *   FieldAddLocationCard  "About this location (n)" — at the top of every
 *                         visit, above "What you'll do today" (pick 1)
 *   FieldAddCustomerLine  "About this customer (n) · Tap to read" (pick 1)
 *   FieldAddDeviceExtras  on each device row: "2 notes" and a small + that
 *                         opens the flow with that device already picked
 *
 * All of it renders nothing unless the server says the switch is on for this
 * person (`useFieldAddJob(...).data.on`).
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, FileText, Image as ImageIcon, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldAddItem } from "./field-add-api";
import { PLACES, shortDate } from "./places";

export function FieldAddButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid="field-add-fab"
            aria-label="Add a photo, note or file"
            className="fixed bottom-20 right-[84px] z-40 flex h-14 w-14 flex-col items-center justify-center rounded-full bg-green-700 text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
            <Plus className="h-7 w-7" strokeWidth={3} aria-hidden />
            <span className="-mt-1 text-[10px] font-bold leading-none">Add</span>
        </button>
    );
}

function ItemLine({ item, onOpenFile }: { item: FieldAddItem; onOpenFile?: (item: FieldAddItem) => void }) {
    const by = [item.authorName?.split(" ")[0], shortDate(item.createdAt)].filter(Boolean).join(", ");
    if (item.kind === "note") {
        return (
            <p className="text-sm leading-snug text-foreground">
                “{item.body}” <span className="text-foreground/70">— {by}</span>
            </p>
        );
    }
    const Icon = item.kind === "photo" ? ImageIcon : FileText;
    return (
        <button
            type="button"
            onClick={() => onOpenFile?.(item)}
            className="flex items-center gap-1.5 text-left text-sm text-foreground underline-offset-2 hover:underline"
        >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            <span>
                {item.kind === "photo" ? "Photo" : (item.fileName ?? "File")}
                {item.body ? `: ${item.body}` : ""} <span className="text-foreground/70">— {by}</span>
            </span>
        </button>
    );
}

export function FieldAddLocationCard({
    items,
    onOpenFile,
}: {
    items: FieldAddItem[];
    onOpenFile?: (item: FieldAddItem) => void;
}) {
    if (items.length === 0) return null;
    const p = PLACES.location;
    return (
        <div className={cn("space-y-1.5 rounded-xl border-2 p-3", p.on)} data-testid="field-add-location-card">
            <div className={cn("flex items-center gap-1.5 text-sm font-bold", p.ink)}>
                <p.Icon className="h-4 w-4" aria-hidden />
                About this location ({items.length})
            </div>
            {items.slice(0, 6).map((i) => (
                <ItemLine key={i.id} item={i} onOpenFile={onOpenFile} />
            ))}
            {items.length > 6 && <p className="text-xs text-foreground/70">And {items.length - 6} older</p>}
        </div>
    );
}

export function FieldAddCustomerLine({
    items,
    onOpenFile,
}: {
    items: FieldAddItem[];
    onOpenFile?: (item: FieldAddItem) => void;
}) {
    const [open, setOpen] = useState(false);
    if (items.length === 0) return null;
    const p = PLACES.customer;
    return (
        <div className="rounded-xl border bg-card p-3" data-testid="field-add-customer-line">
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 text-left"
            >
                <span className={cn("flex items-center gap-1.5 text-sm font-bold", p.ink)}>
                    <p.Icon className="h-4 w-4" aria-hidden />
                    About this customer ({items.length})
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground/70">
                    {open ? "Hide" : "Tap to read"}
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </span>
            </button>
            {open && (
                <div className="mt-2 space-y-1.5">
                    {items.map((i) => (
                        <ItemLine key={i.id} item={i} onOpenFile={onOpenFile} />
                    ))}
                </div>
            )}
        </div>
    );
}

/** On a device row: past-visit notes for this device and its own small +. */
export function FieldAddDeviceExtras({
    items,
    onAdd,
    onOpenFile,
}: {
    items: FieldAddItem[];
    onAdd: (() => void) | null;
    onOpenFile?: (item: FieldAddItem) => void;
}) {
    const [open, setOpen] = useState(false);
    const p = PLACES.device;
    const notes = items.filter((i) => i.kind === "note").length;
    const files = items.length - notes;
    const count = [
        notes ? `${notes} note${notes === 1 ? "" : "s"}` : null,
        files ? `${files} photo${files === 1 ? "" : "s"}` : null,
    ]
        .filter(Boolean)
        .join(", ");
    if (items.length === 0 && !onAdd) return null;
    return (
        <div className="pb-2 pl-[42px]" data-testid="field-add-device-extras">
            <div className="flex flex-wrap items-center gap-2">
                {items.length > 0 && (
                    <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setOpen((o) => !o)}
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold",
                            p.pill,
                        )}
                    >
                        <p.Icon className="h-3 w-3" aria-hidden />
                        {count}
                        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                )}
                {onAdd && (
                    <button
                        type="button"
                        onClick={onAdd}
                        data-testid="field-add-device-plus"
                        aria-label="Add a photo or note to this device"
                        className="inline-flex h-9 items-center gap-1 rounded-full bg-green-700 pl-2 pr-3 text-xs font-bold text-white"
                    >
                        <Plus className="h-4 w-4" strokeWidth={3} aria-hidden />
                        Add to this device
                    </button>
                )}
            </div>
            {open && items.length > 0 && (
                <div className={cn("mt-2 space-y-1.5 rounded-lg border-2 p-2.5", p.on)}>
                    <div className={cn("text-xs font-bold", p.ink)}>
                        From past visits on this device ({items.length})
                    </div>
                    {items.map((i) => (
                        <ItemLine key={i.id} item={i} onOpenFile={onOpenFile} />
                    ))}
                </div>
            )}
        </div>
    );
}
